/**
 * The i18n chokepoint, LEGACY EDITION.
 *
 * The portal confines i18next to `shared/lib/language`; every component asks
 * `useT(ns)` for a lookup function and never writes a literal. This app is
 * Norwegian-only and carries no i18n library, so the chokepoint is kept — same
 * exported surface, same call sites — and only its internals differ: the
 * bundles are read straight from the copied JSON, with `{{name}}` filled in.
 *
 * ⚠️ Keeping the SURFACE is the point. It means a copied primitive needs no
 * edit, so re-copying one from the portal stays a file copy. If this app ever
 * gains a second language, this file is the only one that changes.
 */
import common from './locales/common.json'
import errors from './locales/errors.json'
import forms from './locales/forms.json'

type Bundle = Record<string, string>

const bundles: Record<string, Bundle> = {
  common: common as Bundle,
  errors: errors as Bundle,
  forms: forms as Bundle,
}

export type TranslationValues = Record<string, string | number>

/**
 * Resolves one key in one namespace. An unknown key returns the key itself —
 * the same failure mode i18next has with `fallbackLng: false`, so a missing
 * string is visible in the UI rather than silently empty.
 */
export function translate(
  ns: string,
  key: string,
  values?: TranslationValues,
): string {
  const template = bundles[ns]?.[key]
  if (template === undefined) return key
  if (values === undefined) return template
  return template.replace(/\{\{(\w+)\}\}/g, (whole, name: string) =>
    values[name] === undefined ? whole : String(values[name]),
  )
}

/** The namespace-scoped lookup hook. Matches the portal's `useT` signature. */
export function useT(ns: string) {
  return (key: string, values?: TranslationValues) => translate(ns, key, values)
}

/**
 * The active locale.
 *
 * ⚠️ It is a CONSTANT here, not a subscription. The portal reads this from
 * i18next and re-renders its callers on a language switch; this app ships one
 * language, so there is nothing to switch and nothing to subscribe to. The
 * value matters all the same: copied primitives collate and format against it
 * (`Intl.Collator(locale)` in `DataTable`, the Aksel date locale in
 * `AkselForm`), and 'nb' is what makes æ, ø and å sort after z.
 */
export function getLocale(): string {
	return 'nb';
}

/** The hook form of `getLocale`. Matches the portal's `useLocale` signature. */
export function useLocale(): string {
	return 'nb';
}
